import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Alert, AlertDescription } from "../components/ui/alert";
import axios from "axios";
import { 
  CheckCircle, 
  Download, 
  Mail, 
  FileText, 
  ArrowRight,
  Loader2,
  AlertTriangle,
  Home
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [pollingAttempts, setPollingAttempts] = useState(0);

  const maxPollingAttempts = 5;
  const pollInterval = 2000; // 2 seconds

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus(sessionId, 0);
    } else {
      setError("No payment session found");
      setLoading(false);
    }
  }, [sessionId]);

  const pollPaymentStatus = async (sessionId, attempts) => {
    if (attempts >= maxPollingAttempts) {
      setError('Payment status check timed out. Please check your email for confirmation.');
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/checkout/status/${sessionId}`);
      const status = response.data;
      
      setPaymentStatus(status);
      setPollingAttempts(attempts + 1);
      
      if (status.payment_status === 'paid') {
        setLoading(false);
        return;
      } else if (status.status === 'expired') {
        setError('Payment session expired. Please try again.');
        setLoading(false);
        return;
      }

      // If payment is still pending, continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setError('Error checking payment status. Please try again.');
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    try {
      // In a real implementation, you'd get the generation_id from the payment status
      // For now, we'll use a mock download
      const response = await axios.get(`${API}/download/mock-generation-id`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'Legal_Documents.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Download error:', error);
      alert('Download will be available shortly. Please check your email for documents.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-orange-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Processing Your Payment</h2>
          <p className="text-gray-600">Please wait while we confirm your payment...</p>
          <div className="mt-4">
            <div className="flex items-center justify-center space-x-2">
              <div className="w-2 h-2 bg-orange-500 rounded-full animate-bounce"></div>
              <div className="w-2 h-2 bg-orange-500 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
              <div className="w-2 h-2 bg-orange-500 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
            </div>
          </div>
          <p className="text-sm text-gray-500 mt-4">
            Attempt {pollingAttempts + 1} of {maxPollingAttempts}
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center max-w-md mx-auto">
          <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-slate-900 mb-4">Payment Status Unknown</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <div className="space-y-4">
            <Button 
              onClick={() => window.location.reload()}
              className="bg-orange-500 hover:bg-orange-600 text-white"
            >
              Check Again
            </Button>
            <Button 
              variant="outline"
              onClick={() => navigate('/help')}
            >
              Contact Support
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const isPaymentSuccessful = paymentStatus?.payment_status === 'paid';

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {isPaymentSuccessful ? (
          <>
            {/* Success Header */}
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-2">
                Payment Successful!
              </h1>
              <p className="text-xl text-gray-600">
                Thank you for your purchase. Your legal documents are ready.
              </p>
            </div>

            {/* Payment Details */}
            <Card className="mb-8">
              <CardHeader>
                <CardTitle className="flex items-center">
                  <CheckCircle className="h-5 w-5 mr-2 text-green-600" />
                  Payment Confirmation
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-2">Payment Details</h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Amount:</span>
                        <span className="font-medium">${(paymentStatus.amount_total / 100).toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Currency:</span>
                        <span className="font-medium">{paymentStatus.currency.toUpperCase()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Status:</span>
                        <Badge className="bg-green-100 text-green-800">Paid</Badge>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Transaction ID:</span>
                        <span className="font-medium text-xs">{sessionId.slice(-8)}</span>
                      </div>
                    </div>
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-2">Service Information</h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Service:</span>
                        <span className="font-medium">{paymentStatus.metadata?.service_title || 'Legal Document'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Date:</span>
                        <span className="font-medium">{new Date().toLocaleDateString()}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Next Steps */}
            <Card className="mb-8">
              <CardHeader>
                <CardTitle className="flex items-center">
                  <FileText className="h-5 w-5 mr-2 text-blue-600" />
                  Your Documents Are Ready
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <Alert className="border-green-200 bg-green-50">
                    <Mail className="h-4 w-4 text-green-600" />
                    <AlertDescription className="text-green-800">
                      <strong>Email Sent!</strong> Your receipt and legal documents have been sent to your email address. 
                      Please check your inbox (and spam folder) for the confirmation email.
                    </AlertDescription>
                  </Alert>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="p-4 border rounded-lg">
                      <h4 className="font-semibold text-slate-900 mb-2">What You Received:</h4>
                      <ul className="text-sm text-gray-600 space-y-1">
                        <li>• Complete legal documents (PDF)</li>
                        <li>• State-specific forms</li>
                        <li>• Step-by-step filing instructions</li>
                        <li>• Required forms checklist</li>
                        <li>• Court contact information</li>
                      </ul>
                    </div>
                    <div className="p-4 border rounded-lg">
                      <h4 className="font-semibold text-slate-900 mb-2">Next Steps:</h4>
                      <ul className="text-sm text-gray-600 space-y-1">
                        <li>1. Review all documents carefully</li>
                        <li>2. Sign where indicated</li>
                        <li>3. Gather required supporting documents</li>
                        <li>4. File with appropriate court/agency</li>
                        <li>5. Pay required filing fees</li>
                      </ul>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-4 pt-4">
                    <Button 
                      onClick={handleDownload}
                      className="bg-blue-600 hover:bg-blue-700 text-white flex-1"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Download Documents
                    </Button>
                    <Button 
                      variant="outline"
                      onClick={() => navigate('/help')}
                      className="flex-1"
                    >
                      Get Filing Help
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Additional Actions */}
            <div className="text-center">
              <div className="space-y-4">
                <p className="text-gray-600">
                  Need more legal documents? Browse our complete service catalog.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button 
                    onClick={() => navigate('/services')}
                    className="bg-orange-500 hover:bg-orange-600 text-white"
                  >
                    Browse More Services
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => navigate('/')}
                  >
                    <Home className="h-4 w-4 mr-2" />
                    Back to Home
                  </Button>
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="text-center">
            <AlertTriangle className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-slate-900 mb-4">Payment Status Unclear</h2>
            <p className="text-gray-600 mb-6">
              We're still processing your payment. Please check your email for confirmation.
            </p>
            <Button onClick={() => navigate('/help')}>
              Contact Support
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccess;